import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, KeyRound, MapPin, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import Testimonials from "@/components/Testimonials";
import ListingCards from "@/components/ListingCards";
import SearchPanel from "@/components/SearchPanel";
import { FadeUp, MaskedLines, Stagger, StaggerItem } from "@/components/Reveal";
import { Eyebrow, LuxeButton, ChapterHeading } from "@/components/shared";

const IMG = {
  hero: "https://images.unsplash.com/photo-1505843513577-22bb7d21e455?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHxffG1hbnNpb24lMjBkdXNrfGVufDB8fHx8MTc4ODk2MTAyOHww&ixlib=rb-4.1.0&q=85",
  buying: "/images/interiors/pink-living.jpg",
  selling: "/images/interiors/teal-dining.jpg",
  dc: "https://images.unsplash.com/photo-1762811054947-605b20298615?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHx8bHV4dXJ5JTIwaG91c2V8ZW58MHx8fHwxNzg4OTYxMDI4fDA&ixlib=rb-4.1.0&q=85",
  pool: "https://images.unsplash.com/photo-1757356657991-c3fd6e2e812e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHx8bHV4dXJ5JTIwcG9vbHxlbnwwfHx8fDE3ODg5NjEwMjh8MA&ixlib=rb-4.1.0&q=85",
};

const orgSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["Organization", "RealEstateAgent"],
      name: "Scherif Real Estate — Soby & Associates",
      telephone: "+1-240-372-4804",
      email: "Sobeida@realestatebyscherif.com",
      areaServed: ["Washington, DC", "Maryland", "Virginia", "Dominican Republic"],
      url: typeof window !== "undefined" ? window.location.origin : undefined,
      sameAs: [
        "https://www.zillow.com/profile/Sobeida%20Scherif",
        "https://www.instagram.com/soldbysoby/",
        "https://maps.google.com/?cid=3715564280465972471",
      ],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "5.0",
        reviewCount: "9",
      },
    },
  ],
};

const HERO_SLIDES = [
  { src: "/images/interiors/pink-living.jpg", alt: "Colorful designer living room with pink millwork and blue velvet seating", label: "N°01 — The Living Room" },
  { src: "/images/interiors/teal-dining.jpg", alt: "Dining room with pastoral mural wallpaper and teal millwork", label: "N°02 — The Dining Room" },
  { src: "/images/interiors/blue-salon.jpg", alt: "Deep blue salon with mural wallpaper and velvet armchairs", label: "N°03 — The Salon" },
  { src: "/images/homes/dmv-1.jpg", alt: "Brick colonial home from the Scherif Real Estate portfolio", label: "N°04 — From Our Portfolio" },
  { src: IMG.hero, alt: "Luxury estate at dusk in the Washington DC region", label: "N°05 — The Estate" },
];

const Home = () => {
  const heroRef = useRef(null);
  const [slideIdx, setSlideIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlideIdx((s) => (s + 1) % HERO_SLIDES.length), 6500);
    return () => clearInterval(t);
  }, []);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <>
      <Seo
        title="Scherif Real Estate | Luxury Real Estate in Washington DC, Maryland & Virginia"
        siteName="Scherif Real Estate"
        description="Soby & Associates — exceptional luxury real estate representation across Washington, DC, Maryland, Virginia, and the Dominican Republic. Call 240-372-4804."
        image="/images/soby-portrait.webp"
        jsonLd={orgSchema}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-end overflow-hidden bg-obsidian" data-testid="home-hero">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <motion.div
            className="absolute inset-0"
            initial={{ scale: 1.12, opacity: 0.5 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 2.8, ease: [0.25, 1, 0.35, 1] }}
          >
            {HERO_SLIDES.map((slide, i) => (
              <motion.div
                key={slide.src}
                className="absolute inset-0"
                initial={false}
                animate={{ opacity: i === slideIdx ? 1 : 0 }}
                transition={{ duration: 1.6, ease: [0.25, 1, 0.35, 1] }}
                aria-hidden={i !== slideIdx}
              >
                <motion.img
                  src={slide.src}
                  alt={slide.alt}
                  className="h-[115%] w-full object-cover"
                  initial={false}
                  animate={{ scale: i === slideIdx ? 1.07 : 1 }}
                  transition={{ duration: 7.5, ease: "linear" }}
                />
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian/85 via-obsidian/25 to-obsidian/25" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian/55 via-obsidian/15 to-transparent" aria-hidden="true" />

        <motion.div style={{ opacity: fade }} className="container-luxe relative z-10 pb-28 pt-40 sm:pb-32">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="eyebrow-dark mb-8 flex items-center gap-4"
          >
            <span className="inline-block h-px w-10 bg-champagne" aria-hidden="true" />
            Washington, DC • Maryland • Virginia
          </motion.p>
          <h1 className="max-w-4xl font-serif text-5xl font-normal leading-[1.1] tracking-tight text-ivory sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            <MaskedLines delay={0.35} lines={["Exceptional Real Estate.", "Exceptional Representation."]} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 1 }}
            className="mt-8 max-w-xl text-base font-light leading-relaxed text-ivory/85 sm:text-lg"
          >
            Luxury real estate throughout Washington, DC, Maryland, and Virginia.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 1 }}
            className="mt-12 flex flex-col gap-4 sm:flex-row"
          >
            <LuxeButton to="/listings" variant="light" testId="hero-explore-listings-btn">Explore Our Listings</LuxeButton>
            <LuxeButton to="/list-with-us" variant="outline-light" testId="hero-list-with-us-btn">List With Us</LuxeButton>
          </motion.div>
        </motion.div>

        {/* Slide indicators */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-28 right-5 z-10 hidden flex-col items-end gap-4 sm:right-10 md:flex lg:right-16"
          data-testid="hero-slideshow-controls"
        >
          <AnimatePresence mode="wait">
            <motion.p
              key={slideIdx}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5, ease: [0.25, 1, 0.35, 1] }}
              className="font-serif text-lg italic text-ivory/80"
              data-testid="hero-slide-caption"
            >
              {HERO_SLIDES[slideIdx].label}
            </motion.p>
          </AnimatePresence>
          <div className="flex items-center gap-4">
            <span className="font-mono text-[10px] tracking-[0.3em] text-ivory/70" data-testid="hero-slide-index">
              0{slideIdx + 1}
            </span>
            <span className="relative block h-px w-20 overflow-hidden bg-white/25" aria-hidden="true">
              <motion.span
                key={slideIdx}
                className="absolute inset-y-0 left-0 block bg-champagne"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 6.5, ease: "linear" }}
              />
            </span>
            <span className="font-mono text-[10px] tracking-[0.3em] text-ivory/40">0{HERO_SLIDES.length}</span>
          </div>
          <div className="flex items-center gap-2.5" role="tablist" aria-label="Hero slides">
            {HERO_SLIDES.map((s, i) => (
              <button
                key={s.src}
                onClick={() => setSlideIdx(i)}
                aria-label={`Show slide ${i + 1}`}
                aria-selected={i === slideIdx}
                role="tab"
                data-testid={`hero-slide-dot-${i + 1}`}
                className={`h-px transition-all duration-700 ${i === slideIdx ? "w-10 bg-champagne" : "w-5 bg-white/30 hover:bg-white/60"}`}
              />
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 1 }}
          className="absolute bottom-0 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-3 pb-2 md:flex"
          aria-hidden="true"
        >
          <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-ivory/60">Scroll</span>
          <span className="block h-14 w-px animate-scroll-line bg-champagne" />
        </motion.div>
      </section>

      <Marquee items={["Washington, DC", "Maryland", "Virginia", "Dominican Republic", "Luxury Estates", "Soby & Associates"]} />

      {/* FIND YOUR PLACE — IDX SEARCH */}
      <section className="container-luxe py-24 sm:py-32" data-testid="find-your-place">
        <div className="grid items-start gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <FadeUp>
              <Eyebrow className="mb-4">The Search</Eyebrow>
              <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-charcoal sm:text-5xl">Find Your Place</h2>
              <p className="mt-6 max-w-sm text-base leading-relaxed text-slate-muted">
                Explore exceptional properties across DC, Maryland, and Virginia — live from the MLS.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-8">
            <div className="border border-warm-border bg-white p-6 shadow-[0_20px_60px_rgba(0,0,0,0.05)] sm:p-10">
              <SearchPanel testIdPrefix="home-search" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FEATURED PROPERTIES */}
      <section className="relative overflow-hidden bg-obsidian py-28 sm:py-36" data-testid="featured-properties">
        <div className="container-luxe">
          <div className="max-w-2xl">
            <ChapterHeading number="01" eyebrow="The Portfolio" title="Featured Properties" dark />
            <FadeUp delay={0.2}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-ivory/70">
                Our active portfolio, live from the MLS — always current, always real. Browse the newest listings across the region, presented in
                full detail.
              </p>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <LuxeButton to="/listings" variant="gold" testId="featured-view-all-btn">View All Listings</LuxeButton>
                <LuxeButton to="/listings?tab=sold" variant="outline-light" testId="featured-idx-btn">
                  Recently Sold <ArrowRight size={12} aria-hidden="true" />
                </LuxeButton>
              </div>
            </FadeUp>
          </div>

          <div className="mt-16">
            <ListingCards kind="featured" limit={3} dark heading="Active Now — Live From the MLS" />
          </div>
        </div>
      </section>

      {/* RECENTLY SOLD — editorial band */}
      <section className="border-y border-warm-border bg-stone-beige py-24 sm:py-32" data-testid="recently-sold-section">
        <div className="container-luxe">
          <FadeUp className="mb-14 flex flex-wrap items-end justify-between gap-8">
            <div className="max-w-xl">
              <Eyebrow className="mb-4">Track Record</Eyebrow>
              <h2 className="font-serif text-4xl font-normal leading-[1.1] tracking-tight text-charcoal sm:text-5xl">Recently Sold</h2>
              <p className="mt-5 text-base leading-relaxed text-slate-muted">
                A record of results across the region — every closing a client moved confidently forward.
              </p>
            </div>
            <LuxeButton to="/listings?tab=sold" variant="dark" testId="view-sold-portfolio-btn">View Sold Portfolio</LuxeButton>
          </FadeUp>
          <ListingCards kind="soldpending" limit={3} heading="Live From the MLS" />
        </div>
      </section>

      {/* BUYING */}
      <section className="container-luxe py-24 sm:py-36" data-testid="buying-section">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-20">
          <FadeUp className="order-2 lg:order-1 lg:col-span-6">
            <div className="img-zoom">
              <img src={IMG.buying} alt="Elegant luxury interior — buying a home in Washington DC" className="aspect-[4/5] w-full object-cover" loading="lazy" />
            </div>
          </FadeUp>
          <div className="order-1 lg:order-2 lg:col-span-6">
            <ChapterHeading number="02" eyebrow="Buying" title="Find a home worth coming home to." />
            <FadeUp delay={0.2}>
              <p className="mt-8 text-base leading-relaxed text-slate-muted">
                Finding the right property is about more than location. It is about understanding your goals, recognizing opportunity, and having an
                experienced advocate by your side.
              </p>
              <p className="mt-5 text-base leading-relaxed text-slate-muted">
                Scherif Real Estate provides personalized guidance throughout the buying process — from identifying the right property to negotiating
                the strongest possible terms and successfully closing the transaction.
              </p>
              <div className="mt-10">
                <LuxeButton to="/listings" variant="dark" testId="buying-start-search-btn">Start Your Search</LuxeButton>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* SELLING */}
      <section className="bg-stone-beige py-24 sm:py-36" data-testid="selling-section">
        <div className="container-luxe grid items-center gap-12 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-6">
            <ChapterHeading number="03" eyebrow="Selling" title="Your property deserves exceptional representation." />
            <FadeUp delay={0.2}>
              <p className="mt-8 text-base leading-relaxed text-slate-muted">
                Selling a home requires more than putting a property on the market. It requires strategic positioning, sophisticated marketing,
                expert negotiation, and a deep understanding of the local market.
              </p>
              <p className="mt-5 text-base leading-relaxed text-slate-muted">
                Our team combines market expertise, targeted marketing strategies, and skilled negotiation to position your property for the
                strongest possible result.
              </p>
              <div className="mt-10">
                <LuxeButton to="/list-with-us" variant="dark" testId="selling-sell-with-us-btn">Sell With Us</LuxeButton>
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-6">
            <div className="img-zoom lg:translate-y-10">
              <img src={IMG.selling} alt="Refined living room with fireplace — selling a luxury home in the DMV" className="aspect-[4/5] w-full object-cover" loading="lazy" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ABOUT SOBY */}
      <section className="container-luxe py-24 sm:py-36" data-testid="about-soby-section">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-20">
          <FadeUp className="lg:col-span-5">
            <div className="relative">
              <div className="img-zoom border border-warm-border p-3">
                <img src="/images/soby-portrait.webp" alt="Sobeida “Soby” Scherif — founder of Scherif Real Estate" className="aspect-[4/5] w-full object-cover" loading="lazy" />
              </div>
              <div className="absolute -bottom-6 -right-4 bg-charcoal px-8 py-5 sm:-right-8">
                <p className="font-serif text-4xl font-light text-champagne">25<span className="text-2xl">+</span></p>
                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.3em] text-ivory/70">Years of Experience</p>
              </div>
            </div>
          </FadeUp>
          <div className="lg:col-span-7 lg:pl-8">
            <ChapterHeading number="04" eyebrow="The Founder" title={<>Meet Sobeida “Soby” Scherif</>} />
            <FadeUp delay={0.2}>
              <p className="mt-8 text-base leading-relaxed text-slate-muted">
                With more than 25 years of experience in real estate, Soby Scherif is committed to being the trusted advisor her clients deserve. She
                understands that buying or selling a home is a significant and personal journey — one that carries both financial and emotional
                weight.
              </p>
              <p className="mt-5 text-base leading-relaxed text-slate-muted">
                Two years ago, Soby founded Scherif Real Estate to expand internationally — honing her brand and growing her clientele through a
                clear vision and a consistent approach built on directness, transparency, and expert negotiation.
              </p>
              <div className="mt-10">
                <LuxeButton to="/about" variant="dark" testId="meet-soby-btn">Meet Soby</LuxeButton>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* THE SCHERIF DIFFERENCE */}
      <section className="border-y border-warm-border bg-white py-24 sm:py-32" data-testid="scherif-difference">
        <div className="container-luxe">
          <FadeUp className="mb-16 text-center">
            <Eyebrow className="mb-4 justify-center">Why Scherif</Eyebrow>
            <h2 className="font-serif text-4xl font-light tracking-tight text-charcoal sm:text-5xl">The Scherif Difference</h2>
          </FadeUp>
          <Stagger className="grid gap-12 md:grid-cols-3 md:gap-8 lg:gap-14">
            {[
              { icon: KeyRound, n: "01", title: "Exclusive Service", copy: "We represent residential properties throughout Washington, DC, Maryland, and Virginia, providing every client with the highest level of service to help achieve their goals." },
              { icon: MapPin, n: "02", title: "Location Is Everything", copy: "With exceptional integrity, negotiating skills, and marketing strategies, we work to position every property for the strongest possible result." },
              { icon: ShieldCheck, n: "03", title: "Guaranteed Service", copy: "Our team combines experience, professionalism, and personalized attention to keep every transaction moving smoothly from beginning to end." },
            ].map(({ icon: Icon, n, title, copy }) => (
              <StaggerItem key={n} className="group border-t border-charcoal/10 pt-8" >
                <div className="flex items-center justify-between">
                  <Icon size={26} strokeWidth={1} className="text-champagne transition-transform duration-700 group-hover:-translate-y-1" aria-hidden="true" />
                  <span className="font-serif text-3xl font-light italic text-charcoal/15">{n}</span>
                </div>
                <h3 className="mt-6 font-mono text-xs uppercase tracking-[0.3em] text-charcoal">{title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-muted">{copy}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="container-luxe py-24 sm:py-36" data-testid="service-areas">
        <FadeUp className="mb-14 max-w-3xl">
          <Eyebrow className="mb-4">Where We Work</Eyebrow>
          <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-charcoal sm:text-5xl">Local Expertise. Regional Reach.</h2>
          <p className="mt-6 text-base leading-relaxed text-slate-muted">
            From the nation&apos;s capital to Maryland&apos;s most sought-after communities and Northern Virginia&apos;s premier neighborhoods,
            Scherif Real Estate provides knowledgeable representation throughout the region.
          </p>
        </FadeUp>
        <Stagger className="grid gap-5 md:grid-cols-3">
          {[
            { name: "Washington, DC", img: IMG.dc, tall: false },
            { name: "Maryland", img: IMG.hero, tall: true },
            { name: "Virginia", img: IMG.pool, tall: false },
          ].map((area) => (
            <StaggerItem key={area.name}>
              <a
                href="/listings"
                data-testid={`area-${area.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`img-zoom group relative block ${area.tall ? "md:-mt-10" : ""}`}
              >
                <img src={area.img} alt={`${area.name} luxury real estate`} className="aspect-[3/4] w-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" aria-hidden="true" />
                <div className="absolute bottom-0 left-0 flex w-full items-center justify-between p-7">
                  <span className="font-serif text-2xl font-light text-ivory">{area.name}</span>
                  <ArrowRight size={18} strokeWidth={1.25} className="text-champagne transition-transform duration-500 group-hover:translate-x-2" aria-hidden="true" />
                </div>
              </a>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-stone-beige py-24 sm:py-32" data-testid="testimonials-section">
        <div className="container-luxe">
          <Testimonials />
        </div>
      </section>

      {/* POPULAR SEARCHES — Sotheby's-style link index */}
      <section className="border-t border-warm-border bg-white py-20 sm:py-24" data-testid="popular-searches">
        <div className="container-luxe">
          <FadeUp className="mb-12">
            <Eyebrow className="mb-4">Begin Exploring</Eyebrow>
            <h2 className="font-serif text-3xl font-normal tracking-tight text-charcoal sm:text-4xl">Popular Searches</h2>
          </FadeUp>
          <Stagger className="grid gap-12 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: "By Community",
                links: ["Georgetown", "Capitol Hill", "Bethesda", "Chevy Chase", "Potomac", "McLean", "Great Falls", "Arlington", "Alexandria"],
                to: "/listings",
              },
              {
                title: "By Lifestyle",
                links: ["Waterfront", "Historic Homes", "New Construction", "Gated Estates", "Golf Communities", "Condominiums"],
                to: "/listings",
              },
              {
                title: "By Destination",
                links: ["Washington, DC", "Maryland", "Virginia", "Cap Cana", "Dominican Republic", "South Carolina"],
                to: "/vacation-homes",
              },
            ].map((group) => (
              <StaggerItem key={group.title}>
                <h3 className="border-b border-charcoal/10 pb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-charcoal">{group.title}</h3>
                <ul className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3">
                  {group.links.map((label) => (
                    <li key={label}>
                      <a
                        href={group.to}
                        data-testid={`search-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="link-underline text-sm font-light text-slate-muted transition-colors hover:text-charcoal"
                      >
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" data-testid="final-cta">
        <img src={IMG.selling} alt="Luxury interior — ready to make your next move" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-obsidian/70" aria-hidden="true" />
        <div className="container-luxe relative z-10 flex min-h-[70vh] flex-col items-center justify-center py-28 text-center">
          <FadeUp>
            <Eyebrow dark className="mb-6 justify-center">The Next Chapter</Eyebrow>
            <h2 className="max-w-4xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ivory sm:text-6xl">
              Ready to Make Your Next Move?
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-ivory/75">
              Whether you&apos;re buying, selling, or simply exploring what&apos;s possible, we&apos;re here to help you make your next move with
              confidence.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <LuxeButton to="/contact" variant="gold" testId="final-get-in-touch-btn">Get in Touch</LuxeButton>
              <LuxeButton href="tel:+12403724804" variant="outline-light" testId="final-call-btn">Call 240-372-4804</LuxeButton>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default Home;
